import { useToast } from "@/hooks/use-toast";
import { useEffect, useState } from "react";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import type { PaymentInstructionsSettings } from "@shared/schema";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { AlertTriangle, CheckCircle2, CreditCard, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { MaskedSensitiveField } from "./MaskedSensitiveField";

export function PaymentInstructionsPanel() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [form, setForm] = useState({
    preferredMethod: "",
    bankName: "",
    accountName: "",
    accountNumber: "",
    routingNumber: "",
    wireInstructions: "",
    achMemoFormat: "",
    zelleInfo: "",
    notes: "",
  });
  const [initialized, setInitialized] = useState(false);

  const settingsQuery = useQuery<PaymentInstructionsSettings | null>({
    queryKey: ["/api/admin/settings/payment-instructions"],
    queryFn: async () => {
      const res = await fetch("/api/admin/settings/payment-instructions", { credentials: "include" });
      return res.json();
    },
  });

  useEffect(() => {
    if (settingsQuery.data && !initialized) {
      const s = settingsQuery.data;
      setForm({
        preferredMethod: s.preferredMethod || "",
        bankName: s.bankName || "",
        accountName: s.accountName || "",
        accountNumber: s.accountNumber || "",
        routingNumber: s.routingNumber || "",
        wireInstructions: s.wireInstructions || "",
        achMemoFormat: s.achMemoFormat || "",
        zelleInfo: s.zelleInfo || "",
        notes: s.notes || "",
      });
      setInitialized(true);
    }
  }, [settingsQuery.data, initialized]);

  const saveMutation = useMutation({
    mutationFn: async (data: typeof form) => apiRequest("PUT", "/api/admin/settings/payment-instructions", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/settings/payment-instructions"] });
      toast({ title: "Payment instructions saved", description: "These will now be auto-filled on all referral invoices." });
    },
    onError: (err: any) => toast({ title: "Failed to save", description: parseErrorMessage(err, "Couldn't save. Please try again."), variant: "destructive" }),
  });

  const isConfigured = !!(settingsQuery.data && (
    settingsQuery.data.preferredMethod ||
    settingsQuery.data.bankName ||
    settingsQuery.data.accountName ||
    settingsQuery.data.accountNumber ||
    settingsQuery.data.routingNumber ||
    settingsQuery.data.wireInstructions ||
    settingsQuery.data.achMemoFormat ||
    settingsQuery.data.zelleInfo ||
    settingsQuery.data.notes
  ));

  return (
    <div className="max-w-2xl space-y-4">
      {!settingsQuery.isLoading && !isConfigured && (
        <div className="flex items-start gap-3 rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 px-4 py-3" data-testid="banner-payment-instructions-missing">
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm">
            <p className="font-medium text-amber-800 dark:text-amber-200">Payment instructions not configured</p>
            <p className="text-amber-700 dark:text-amber-300 mt-0.5">Until these are saved, referral invoice PDFs will display a red placeholder and the Send button will be disabled. Configure your payment details below to enable invoice sending.</p>
          </div>
        </div>
      )}
      {isConfigured && (
        <div className="flex items-center gap-2 rounded-md border border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-950/40 px-4 py-2.5" data-testid="banner-payment-instructions-ok">
          <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
          <p className="text-sm text-green-800 dark:text-green-200 font-medium">Payment instructions configured — invoices are ready to send.</p>
        </div>
      )}

      <Card className="p-5 space-y-5">
        <div>
          <h3 className="font-semibold text-sm mb-1">Payment Instructions</h3>
          <p className="text-xs text-muted-foreground">These details are automatically embedded in every referral commission invoice PDF.</p>
        </div>

        {settingsQuery.isLoading ? (
          <div className="space-y-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-9 w-full" />)}</div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Preferred Payment Method</Label>
              <Input className="h-9 mt-1 text-sm" placeholder="e.g. Wire Transfer, ACH, Zelle" value={form.preferredMethod} onChange={e => setForm(f => ({ ...f, preferredMethod: e.target.value }))} data-testid="input-preferred-method" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Bank Name</Label>
                <Input className="h-9 mt-1 text-sm" placeholder="e.g. Chase Bank" value={form.bankName} onChange={e => setForm(f => ({ ...f, bankName: e.target.value }))} data-testid="input-bank-name" />
              </div>
              <div>
                <Label className="text-xs">Account Name</Label>
                <Input className="h-9 mt-1 text-sm" placeholder="Business or personal name" value={form.accountName} onChange={e => setForm(f => ({ ...f, accountName: e.target.value }))} data-testid="input-account-name" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Account Number</Label>
                <MaskedSensitiveField
                  value={form.accountNumber}
                  onChange={(v) => setForm(f => ({ ...f, accountNumber: v }))}
                  placeholder="Account number"
                  testIdBase="account-number"
                />
              </div>
              <div>
                <Label className="text-xs">Routing Number</Label>
                <MaskedSensitiveField
                  value={form.routingNumber}
                  onChange={(v) => setForm(f => ({ ...f, routingNumber: v }))}
                  placeholder="ABA routing number"
                  testIdBase="routing-number"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Wire Instructions</Label>
              <Textarea className="mt-1 text-sm" rows={2} placeholder="Additional wire transfer instructions..." value={form.wireInstructions} onChange={e => setForm(f => ({ ...f, wireInstructions: e.target.value }))} data-testid="input-wire-instructions" />
            </div>
            <div>
              <Label className="text-xs">ACH Memo Format</Label>
              <Input className="h-9 mt-1 text-sm" placeholder='e.g. "REF: {invoice_number}"' value={form.achMemoFormat} onChange={e => setForm(f => ({ ...f, achMemoFormat: e.target.value }))} data-testid="input-ach-memo" />
            </div>
            <div>
              <Label className="text-xs">Zelle (optional)</Label>
              <Input className="h-9 mt-1 text-sm" placeholder="Zelle email or phone" value={form.zelleInfo} onChange={e => setForm(f => ({ ...f, zelleInfo: e.target.value }))} data-testid="input-zelle" />
            </div>
            <div>
              <Label className="text-xs">Additional Notes</Label>
              <Textarea className="mt-1 text-sm" rows={2} placeholder="Any other payment notes for the invoice..." value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} data-testid="input-payment-notes" />
            </div>

            <Button className="w-full" onClick={() => saveMutation.mutate(form)} disabled={saveMutation.isPending} data-testid="button-save-payment-instructions">
              {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <CreditCard className="h-4 w-4 mr-2" /> Save Payment Instructions
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
